import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { useQuery } from 'react-query';
import { useBreadcrumbRoutes, Wrapper } from '@maxtropy/components';
import {
  apiV2PvMonitorPerformanceOverviewPost,
  apiV2PvMonitorPredictOverviewPost,
  apiV2PvMonitorSkyImagerMonitorPost,
  apiV2PvMonitorWeatherStationDataPost,
  apiV2PvStationPvListPost,
} from '@maxtropy/dmes-apis-v2';
import styles from './index.module.scss';
import { adaptForecastData, adaptPerformanceData, adaptSkyMonitorData, adaptWeatherData } from './dataAdapters';
import PageHeader from './components/PageHeader';
import SkyCameraPanel from './components/SkyCameraPanel';
import ForecastPanel from './components/ForecastPanel';
import WeatherPanel from './components/WeatherPanel';
import PRPanel from './components/PRPanel';

const MONITOR_REFRESH_INTERVAL = 60 * 1000;

const PhotovoltaicMonitoringCenter: React.FC = () => {
  const routesContext = useBreadcrumbRoutes();
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [station, setStation] = useState('');
  const { data: stationOptions = [] } = useQuery('pvMonitoringStationList', async () => {
    const res = await apiV2PvStationPvListPost({});
    return (res.list ?? [])
      .filter(item => item.id)
      .map(item => ({
        label: item.name ?? '',
        value: String(item.id),
      }));
  });

  useEffect(() => {
    const firstStation = stationOptions[0]?.value ?? '';

    if (!station && firstStation) {
      setStation(firstStation);
    }
  }, [station, stationOptions]);

  const day = date.startOf('day').valueOf();
  const monitorParams = {
    pvId: station,
    day,
  };
  const queryEnabled = Boolean(station);
  const monitorQueryOptions = {
    enabled: queryEnabled,
    refetchInterval: MONITOR_REFRESH_INTERVAL,
  };

  const { data: skyMonitorData } = useQuery(
    ['pvMonitorSkyImagerMonitor', station, day],
    () => apiV2PvMonitorSkyImagerMonitorPost({ ...monitorParams }),
    monitorQueryOptions
  );
  const { data: forecastData } = useQuery(
    ['pvMonitorPredictOverview', station, day],
    () => apiV2PvMonitorPredictOverviewPost({ ...monitorParams }),
    monitorQueryOptions
  );
  const { data: weatherData } = useQuery(
    ['pvMonitorWeatherStationData', station, day],
    () => apiV2PvMonitorWeatherStationDataPost({ ...monitorParams }),
    monitorQueryOptions
  );
  const { data: performanceData } = useQuery(
    ['pvMonitorPerformanceOverview', station, day],
    () => apiV2PvMonitorPerformanceOverviewPost({ ...monitorParams }),
    monitorQueryOptions
  );

  const skyCameras = adaptSkyMonitorData(skyMonitorData);
  const forecast = adaptForecastData(forecastData);
  const weather = adaptWeatherData(weatherData);
  const pr = adaptPerformanceData(performanceData);

  return (
    <Wrapper routes={routesContext?.routes} className={styles.wrapper} hasPadding={false}>
      <div className={styles.monitoringPage}>
        <PageHeader
          date={date}
          onDateChange={setDate}
          stationOptions={stationOptions}
          stationValue={station}
          onStationChange={setStation}
        />
        <main className={styles.contentLayout}>
          <div className={styles.leftTop}>
            <SkyCameraPanel skyCameras={skyCameras} />
          </div>
          <div className={styles.rightTop}>
            <ForecastPanel forecast={forecast} selectedDate={date} />
          </div>
          <div className={styles.leftBottom}>
            <WeatherPanel weather={weather} />
          </div>
          <div className={styles.rightBottom}>
            <PRPanel pr={pr} />
          </div>
        </main>
      </div>
    </Wrapper>
  );
};

export default PhotovoltaicMonitoringCenter;
